<template>
  <div
    id="viewer"
    class="scanned-pdf-reader"
  />
</template>

<script>
import WebViewer from '@pdftron/pdfjs-express-viewer';
import { getInstance } from '@pdftron/pdfjs-express-viewer';

/**
 * ScannedPDFViewer component for displaying scanned PDFs with OCR overlay
 * Handles canvas-based polygon overlays and OCR-based text selection
 */
export default {
    name: 'ScannedPDFViewer',
    props: {
        pdfFile: {
            type: Blob,
            required: true
        },
        parsedLines: {
            type: Array,
            default: () => []
        },
        pageGroupedLines: {
            type: Object,
            default: () => ({})
        },
        availablePages: {
            type: Array,
            default: () => []
        },
        disableHoverEffects: {
            type: Boolean,
            default: false
        },
        activePolygonIndices: {
            type: Array,
            default: null
        }
    },
    emits: ['documentLoaded', 'polygonClicked', 'pageChanged'],
    data() {
        return {
            docViewer: null,
            pdfInstance: null,
            currentPage: 0,
            canvasesByPage: {},
            canvasContext: null,
            canvas: null,
            currentHoveredPolygonIdx: null,
            resizeObserver: null,
            windowResizeHandler: null,
            zoomUpdateTimeout: null,
            selectionStarted: false,
            startLocation: null,
            endLocation: null,
        };
    },

    watch: {
        pdfFile: {
            handler() {
                this.$nextTick(() => {
                    this.initializeViewer();
                });
            },
            immediate: false
        },
        parsedLines: {
            handler(newParsedLines) {
                if (newParsedLines && newParsedLines.length > 0 && this.docViewer) {
                    console.log("Parsed lines updated, initializing canvas overlay");
                    this.$nextTick(() => {
                        this.initializeCanvasOverlay();
                    });
                }
            },
            deep: true
        },
        disableHoverEffects(newVal) {
            // Disable pointer events on canvas when a dialog is open
            this.updateCanvasPointerEvents();
        },
        activePolygonIndices() {
            const canvas = this.canvasesByPage[this.currentPage];
            if (canvas) {
                this.drawPolygonsOnCanvasForPage(this.currentPage, canvas);
            }
        }
    },

    mounted() {
        if (this.pdfFile) {
            this.initializeViewer();
        }
    },

    beforeUnmount() {
        this.dispose();
    },

    methods: {
        async initializeViewer() {
            const element = document.getElementById('viewer');
            const instance = getInstance();

            if (instance) {
                instance.dispose();
            }

            try {
                await WebViewer({
                    licenseKey: process.env.VUE_APP_PDF_VIEWER_KEY,
                    path: `${process.env.BASE_URL}public`
                }, element).then((instance) => {
                    instance.UI.loadDocument(this.pdfFile, { filename: 'viewing_file.pdf' });
                    this.docViewer = instance.Core.documentViewer;
                    this.pdfInstance = instance;

                    this.setupZoomListener(instance);
                    this.setupPageChangeListener();

                    this.docViewer.addEventListener('documentLoaded', () => {
                        console.log("Scanned PDF document loaded");
                        // Defer layout/tool mode until after the UI's own documentLoaded
                        // handler completes (it runs after Core's and resets the layout to default)
                        setTimeout(() => {
                            instance.UI.setLayoutMode(instance.UI.LayoutMode.Continuous);
                            instance.UI.setFitMode(instance.UI.FitMode.FitWidth);
                        }, 5000);
                        if (this.parsedLines.length > 0) {
                            this.$nextTick(() => {
                                this.initializeCanvasOverlay();
                            });
                        }

                        this.$emit('documentLoaded');
                    });
                });
            } catch (error) {
                throw error;
            }
        },

        initializeCanvasOverlay() {
            this.canvasesByPage = {};
            document.querySelectorAll('canvas[id^="ocr-canvas-"]').forEach(canvas => {
                canvas.remove();
            });

            const iframe = document.querySelector('iframe[id^="webviewer"]');
            if (!iframe) {
                return;
            }

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

            // List all page elements to find the correct ID pattern
            const allPageElements = iframeDocument.querySelectorAll('[id*="page"], [id*="section"]');

            for (const page of this.availablePages) {
                // Try different page ID patterns
                let pageElement = iframeDocument.getElementById(`pageSection${page}`);
                if (!pageElement) pageElement = iframeDocument.getElementById(`page${page}`);
                if (!pageElement) pageElement = iframeDocument.getElementById(`page${page + 1}`); // Try 1-indexed
                
                // If still not found, try finding by searching class names for the page number
                if (!pageElement) {
                    const pagesByClass = iframeDocument.querySelectorAll('[class*="page"]');
                    for (const el of pagesByClass) {
                        const classStr = el.getAttribute('class') || '';
                        if (classStr.includes(`page-${page}`) || classStr.includes(`page${page}`)) {
                            pageElement = el;
                            break;
                        }
                    }
                }

                if (!pageElement) {
                    continue;
                }

                this.createCanvasForPage(page, pageElement, iframeDocument);
            }

            this.$nextTick(() => {
                this.setupCanvasResizeTracking();
                this.setupWindowResizeHandler();
            });
        },

        createCanvasForPage(page, pageElement, iframeDocument) {
            // Create canvas in the iframe's document, not the main document
            const canvas = iframeDocument.createElement('canvas');
            canvas.id = `ocr-canvas-${page}`;
            canvas.className = 'ocr-canvas';
            canvas.style.position = 'absolute';
            canvas.style.top = '0';
            canvas.style.left = '0';
            canvas.style.zIndex = '100';
            canvas.style.cursor = 'pointer';
            canvas.style.pointerEvents = 'auto';
            // Add data-test attribute to first canvas for demo targeting
            if (page === 0) {
                canvas.setAttribute('data-test', 'polygon-canvas-0');
            }

            let canvasWidth = pageElement.offsetWidth;
            let canvasHeight = pageElement.offsetHeight;

            if (canvasWidth === 0 || canvasHeight === 0) {
                const computed = iframeDocument.defaultView.getComputedStyle(pageElement);
                canvasWidth = parseInt(computed.width) || pageElement.scrollWidth || 425;
                canvasHeight = parseInt(computed.height) || pageElement.scrollHeight || 604;
            }

            canvas.width = canvasWidth;
            canvas.height = canvasHeight;

            const currentPos = pageElement.style.position;
            if (currentPos === '' || currentPos === 'static') {
                pageElement.style.position = 'relative';
            }

            const currentOverflow = pageElement.style.overflow;
            if (currentOverflow === 'hidden') {
                pageElement.style.overflow = 'visible';
            }

            pageElement.appendChild(canvas);
            this.canvasesByPage[page] = canvas;

            this.drawPolygonsOnCanvasForPage(page, canvas);

            canvas.addEventListener('mousemove', (e) => this.onCanvasMouseMove(e, page, canvas));
            canvas.addEventListener('click', (e) => this.onCanvasClick(e, page, canvas));
        },

        drawPolygonsOnCanvasForPage(page, canvas) {
            if (!canvas) {
                return;
            }

            const ctx = canvas.getContext('2d');
            if (!ctx) {
                return;
            }

            console.log(`\n=== DRAWING POLYGONS FOR PAGE ${page} ===`);
            console.log(`Canvas dimensions: ${canvas.width}x${canvas.height}`);
            console.log(`pageGroupedLines:`, this.pageGroupedLines);
            
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            const polygons = this.pageGroupedLines[page] || [];
            console.log(`Found ${polygons.length} polygons for page ${page}`);

            if (polygons.length === 0) {
                console.warn(`No polygons for page ${page}. Available pages:`, Object.keys(this.pageGroupedLines));
                return;
            }

            let drawnCount = 0;
            polygons.forEach((polygon, idx) => {
                if (polygon.points && polygon.points.length > 0) {
                    this.drawPolygonOnContext(
                        polygon,
                        idx === this.currentHoveredPolygonIdx && page === this.currentPage,
                        ctx,
                        canvas,
                        false,  // isImageResource flag
                        this.activePolygonIndices ? this.activePolygonIndices.includes(idx) : false
                    );
                    drawnCount++;
                }
            });
        },

        drawPolygonOnContext(polygon, isHovered = false, ctx, canvas, isImageResource = false, isVoiceActive = false) {
            if (!ctx) {
                return;
            }
            if (!polygon.points || polygon.points.length < 3) {
                return;
            }

            const points = polygon.points;
            const imageBbox = polygon.imageBbox || [0, 0, 560, 794];
            const bboxX = imageBbox[0];
            const bboxY = imageBbox[1];

            let scaleX = 1.0;
            let scaleY = 1.0;

            if (!isImageResource) {
                const originalWidth = imageBbox[2] - imageBbox[0];
                const originalHeight = imageBbox[3] - imageBbox[1];
                scaleX = canvas.width / originalWidth;
                scaleY = canvas.height / originalHeight;
            }

            ctx.strokeStyle = isVoiceActive ? 'rgba(255, 210, 0, 0.95)' : isHovered ? 'rgba(255, 165, 0, 0.95)' : 'rgba(0,0,0,0)';
            ctx.fillStyle = isVoiceActive ? 'rgba(255, 230, 0, 0.35)' : isHovered ? 'rgba(255, 200, 0, 0.25)' : 'rgba(0,0,0,0)';
            ctx.lineWidth = isVoiceActive || isHovered ? 1 : 0;

            ctx.beginPath();
            const firstX = (points[0].x - bboxX) * scaleX;
            const firstY = (points[0].y - bboxY) * scaleY;
            ctx.moveTo(firstX, firstY);
            
            for (let i = 1; i < points.length; i++) {
                const x = (points[i].x - bboxX) * scaleX;
                const y = (points[i].y - bboxY) * scaleY;
                ctx.lineTo(x, y);
            }
            ctx.closePath();

            ctx.fill();
            ctx.stroke();
        },

        isPointInPolygon(x, y, polygon, canvas, isImageResource = false) {
            const points = polygon.points || [];
            const imageBbox = polygon.imageBbox || [0, 0, 560, 794];
            const bboxX = imageBbox[0];
            const bboxY = imageBbox[1];

            let scaleX = 1.0;
            let scaleY = 1.0;

            if (!isImageResource) {
                const originalWidth = imageBbox[2] - imageBbox[0];
                const originalHeight = imageBbox[3] - imageBbox[1];
                const canvasWidth = canvas ? canvas.width : 425;
                const canvasHeight = canvas ? canvas.height : 604;
                scaleX = canvasWidth / originalWidth;
                scaleY = canvasHeight / originalHeight;
            }

            let inside = false;
            for (let i = 0, j = points.length - 1; i < points.length; j = i++) {
                const xi = (points[i].x - bboxX) * scaleX;
                const yi = (points[i].y - bboxY) * scaleY;
                const xj = (points[j].x - bboxX) * scaleX;
                const yj = (points[j].y - bboxY) * scaleY;

                const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
                if (intersect) inside = !inside;
            }
            return inside;
        },

        onCanvasMouseMove(event, page, canvas) {
            // Skip hover effects if they are disabled (e.g., when a dialog is open)
            if (this.disableHoverEffects) {
                return;
            }

            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            const polygons = this.pageGroupedLines[page] || [];
            let hoveredIdx = null;

            for (let i = 0; i < polygons.length; i++) {
                const polygon = polygons[i];
                if (polygon.points && this.isPointInPolygon(x, y, polygon, canvas)) {
                    hoveredIdx = i;
                    break;
                }
            }

            if (hoveredIdx !== this.currentHoveredPolygonIdx) {
                this.currentHoveredPolygonIdx = hoveredIdx;
                this.drawPolygonsOnCanvasForPage(page, canvas);
            }
        },

        onCanvasClick(event, page, canvas) {
            // Skip click events if they are disabled (e.g., when a dialog is open)
            if (this.disableHoverEffects) {
                return;
            }

            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            const polygons = this.pageGroupedLines[page] || [];

            for (let i = 0; i < polygons.length; i++) {
                const polygon = polygons[i];
                if (polygon.points && this.isPointInPolygon(x, y, polygon, canvas)) {
                    console.log(`Clicked on polygon: "${polygon.text}"`);
                    this.$emit('polygonClicked', {
                        polygon,
                        page,
                        index: i
                    });
                    break;
                }
            }
        },

        setupCanvasResizeTracking() {
            const iframe = document.querySelector('iframe[id^="webviewer"]');
            if (!iframe) return;

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

            this.resizeObserver = new iframe.contentWindow.ResizeObserver((entries) => {
                for (const entry of entries) {
                    const pageId = entry.target.id;
                    const match = pageId.match(/pageSection(\d+)/);
                    if (match) {
                        const page = parseInt(match[1]);
                        this.updateCanvasForPage(page, entry.contentRect.width, entry.contentRect.height);
                    }
                }
            });

            for (const page of this.availablePages) {
                const pageElement = iframeDocument.getElementById(`pageSection${page}`);
                if (pageElement) {
                    this.resizeObserver.observe(pageElement);
                }
            }
        },

        updateCanvasForPage(pageNum, newWidth, newHeight) {
            const canvas = this.canvasesByPage[pageNum];
            if (!canvas) return;

            canvas.width = newWidth;
            canvas.height = newHeight;
            this.drawPolygonsOnCanvasForPage(pageNum, canvas);
        },

        setupWindowResizeHandler() {
            let resizeTimeout;
            this.windowResizeHandler = () => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    for (const page of this.availablePages) {
                        const canvas = this.canvasesByPage[page];
                        if (canvas) {
                            this.drawPolygonsOnCanvasForPage(page, canvas);
                        }
                    }
                }, 200);
            };

            window.addEventListener('resize', this.windowResizeHandler);
        },

        setupZoomListener(instance) {
            if (!instance?.Core?.documentViewer) return;

            const { documentViewer } = instance.Core;
            let lastZoomLevel = documentViewer.getZoom();

            const zoomListener = () => {
                const currentZoom = documentViewer.getZoom();

                if (currentZoom !== lastZoomLevel) {
                    lastZoomLevel = currentZoom;

                    if (this.zoomUpdateTimeout) {
                        clearTimeout(this.zoomUpdateTimeout);
                    }

                    this.zoomUpdateTimeout = setTimeout(() => {
                        for (const page of this.availablePages) {
                            const canvas = this.canvasesByPage[page];
                            if (!canvas) continue;

                            const iframe = document.querySelector('iframe[id^="webviewer"]');
                            if (!iframe) continue;

                            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
                            const pageElement = iframeDocument.getElementById(`pageSection${page}`);

                            if (pageElement) {
                                const newWidth = pageElement.offsetWidth;
                                const newHeight = pageElement.offsetHeight;

                                if (newWidth > 0 && newHeight > 0) {
                                    this.updateCanvasForPage(page, newWidth, newHeight);
                                }
                            }
                        }
                    }, 300);
                }
            };

            documentViewer.addEventListener('zoomUpdated', zoomListener);
        },

        setupPageChangeListener() {
            if (!this.docViewer) return;

            this.docViewer.addEventListener('pageNumberUpdated', (pageNumber) => {
                console.log("PDF page changed to:", pageNumber);
                this.currentPage = pageNumber;
                this.$emit('pageChanged', pageNumber);
                
                // Create canvas overlay for current page and nearby pages if not already created
                this.ensureCanvasOverlayForPages(pageNumber);
            });
        },

        ensureCanvasOverlayForPages(currentPageNumber) {
            if (!this.docViewer) return;

            const iframe = document.querySelector('iframe[id^="webviewer"]');
            if (!iframe) return;

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;

            // Check current page and nearby pages (current ± 2)
            const pagesToCheck = [currentPageNumber - 2, currentPageNumber - 1, currentPageNumber, currentPageNumber + 1, currentPageNumber + 2];

            for (const page of pagesToCheck) {
                // Skip if not in available pages or already created
                if (!this.availablePages.includes(page) || this.canvasesByPage[page]) {
                    continue;
                }

                // Try to find the page element
                let pageElement = iframeDocument.getElementById(`pageSection${page}`);
                if (!pageElement) pageElement = iframeDocument.getElementById(`page${page}`);
                if (!pageElement) pageElement = iframeDocument.getElementById(`page${page + 1}`);

                if (!pageElement) {
                    const pagesByClass = iframeDocument.querySelectorAll('[class*="page"]');
                    for (const el of pagesByClass) {
                        const classStr = el.getAttribute('class') || '';
                        if (classStr.includes(`page-${page}`) || classStr.includes(`page${page}`)) {
                            pageElement = el;
                            break;
                        }
                    }
                }

                // If page element is now available, create canvas overlay for it
                if (pageElement) {
                    this.createCanvasForPage(page, pageElement, iframeDocument);
                }
            }
        },

        getCurrentPage() {
            return this.docViewer?.getCurrentPage() || 0;
        },

        updateCanvasPointerEvents() {
            // Update pointer events on all canvases based on disableHoverEffects
            const canvases = document.querySelectorAll('canvas[id^="ocr-canvas-"]');
            canvases.forEach(canvas => {
                if (this.disableHoverEffects) {
                    canvas.style.pointerEvents = 'none';
                } else {
                    canvas.style.pointerEvents = 'auto';
                }
            });
        },

        dispose() {
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
                this.resizeObserver = null;
            }

            if (this.windowResizeHandler) {
                window.removeEventListener('resize', this.windowResizeHandler);
                this.windowResizeHandler = null;
            }

            if (this.zoomUpdateTimeout) {
                clearTimeout(this.zoomUpdateTimeout);
                this.zoomUpdateTimeout = null;
            }

            const instance = getInstance();
            if (instance) {
                instance.dispose();
            }
        }
    }
};
</script>

<style scoped>
.scanned-pdf-reader {
    width: 100%;
    height: 100%;
    position: relative;
}
</style>
